import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { BrandMark } from "../components/BrandMark";
import { PublicMobileMenu } from "../components/PublicMobileMenu";
import { toast } from "sonner";
import {
    Menu, X, ArrowRight, Sparkles, ShieldCheck, FileText, Heart,
    Users, Briefcase, Wheat, Building2, UsersRound, UserCheck,
    ClipboardCheck, CheckCircle2, FolderCheck, LifeBuoy,
    Facebook, Instagram, Linkedin, MessageCircle,
} from "lucide-react";

/* ---------- shared nav (About Us routes to /about-us; others go to /home[#hash]) ---------- */
const NAV = [
    { label: "Home", to: "/home" },
    { label: "About Us", to: "/about-us" },
    { label: "Loans", to: "/loans" },
    { label: "Saving Scheme", to: "/daily-savings" },
    { label: "Investment", to: "/investment" },
    { label: "Careers", to: "/careers" },
    { label: "Contact Us", to: "/contact-us" },
];

const VALUES = [
    { icon: FileText, title: "Transparency",
        desc: "Clear records, receipts and statements at every step." },
    { icon: Heart, title: "Customer Focus",
        desc: "Support tailored around the needs of the people we serve." },
    { icon: ShieldCheck, title: "Responsible Processes",
        desc: "Structured reviews and considered decisions." },
    { icon: Sparkles, title: "Digital Record Keeping",
        desc: "A single, organised place for account information." },
];

const SERVE = [
    { icon: UserCheck, title: "Individuals" },
    { icon: Briefcase, title: "Small businesses" },
    { icon: Users, title: "Self-employed professionals" },
    { icon: Wheat, title: "Farmers" },
    { icon: UsersRound, title: "Joint-liability groups" },
    { icon: Building2, title: "Salaried professionals" },
];

const APPROACH = [
    { icon: FileText, title: "Clear Information" },
    { icon: ClipboardCheck, title: "Application Review" },
    { icon: CheckCircle2, title: "Approval" },
    { icon: FolderCheck, title: "Digital Account Records" },
    { icon: LifeBuoy, title: "Ongoing Support" },
];

const FOOTER_LINKS = {
    Products: ["Personal Loan", "Business Loan", "Group Loan", "Saving Scheme", "Investment"],
    Company: ["About Us", "Careers", "News & Updates", "Contact Us"],
    Support: ["FAQs", "Grievance Redressal", "Customer Care", "Downloads"],
};

const placeholderLoginToast = () => {
    window.location.assign("/customer-login");
};

/* ---------- page ---------- */
export default function AboutUs() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        document.title = "About Us | Lokdhara Microfinance";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    const go = (to) => {
        if (to) navigate(to);
    };

    return (
        <div id="top" className="min-h-screen bg-white text-slate-900 antialiased">
            {/* HEADER (mirrors /home) --------------------------------------- */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                    <Link to="/home" className="flex items-center" data-testid="about-brand">
                        <BrandMark size={56} className="sm:!h-14" />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-700">
                        {NAV.map((n) => (
                            <button key={n.label}
                                onClick={() => go(n.to)}
                                className={`hover:text-brand-royal transition ${n.to === "/about-us" ? "text-brand-royal" : ""}`}
                                data-testid={`about-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button
                            className="hidden sm:inline-flex bg-brand-royal hover:bg-brand-hover text-white rounded-full h-10 px-5"
                            onClick={placeholderLoginToast}
                            data-testid="about-header-customer-login"
                        >
                            Customer Login
                        </Button>
                        <button
                            aria-label="Open menu"
                            className="lg:hidden h-10 w-10 rounded-lg border border-slate-200 grid place-items-center"
                            onClick={() => setMenuOpen(true)}
                            data-testid="about-mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                {/* mobile drawer */}
                {menuOpen && (
                    <PublicMobileMenu
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        nav={NAV}
                        onNavItemClick={(n) => go(n.to)}
                        onLoginClick={placeholderLoginToast}
                        testId="about-mobile-menu"
                        activePath="/about-us"
                    />
                )}
            </header>

            {/* NAVY PAGE HERO --------------------------------------------- */}
            <section className="relative overflow-hidden bg-brand-navy text-white">
                <div className="absolute inset-0 pointer-events-none opacity-20"
                    style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.1) 0%, transparent 40%)" }}
                    aria-hidden
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-tint bg-white/10 rounded-full px-3 py-1">
                        Who We Are
                    </span>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight max-w-3xl">
                        About Lokdhara Microfinance
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">
                        A community-oriented microfinance service focused on making financial
                        progress simple, structured and accessible.
                    </p>
                </div>
            </section>

            {/* INTRODUCTION ---------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                        Introduction
                    </span>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                        Structured services, transparent records.
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
                        Lokdhara Microfinance is focused on providing structured and accessible
                        financial services through transparent processes, clear records and
                        customer-oriented support.
                    </p>
                </div>
            </section>

            {/* MISSION + IMAGE ------------------------------------------- */}
            <section className="bg-slate-50 py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
                    <div className="relative order-2 lg:order-1">
                        <div className="absolute -inset-3 bg-brand-tint/40 rounded-[2rem] rotate-1" aria-hidden />
                        <img
                            src="/home-community.jpg"
                            alt="Community members standing together"
                            className="relative rounded-[1.75rem] object-cover w-full h-[380px] shadow-card-hover"
                            loading="lazy"
                        />
                    </div>
                    <div className="order-1 lg:order-2">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Our Mission
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Supporting financial progress, responsibly.
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed text-base sm:text-lg">
                            To support financial progress through simple and responsible services —
                            designed around clarity, trust and long-term customer relationships.
                        </p>
                    </div>
                </div>
            </section>

            {/* CORE VALUES ---------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Core Values
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            What guides our work
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {VALUES.map(({ icon: Icon, title, desc }) => (
                            <Card key={title}
                                className="rounded-2xl border-slate-100 p-7 hover:shadow-card-hover transition"
                                data-testid={`value-card-${title.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                    <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <h3 className="mt-5 font-semibold text-slate-900 text-base">{title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO WE SERVE --------------------------------------------- */}
            <section className="bg-brand-soft py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Who We Serve
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Designed for many, personalised for each
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVE.map(({ icon: Icon, title }) => (
                            <Card key={title}
                                className="rounded-2xl border-white bg-white/90 p-6 flex items-center gap-4 hover:shadow-card-hover transition"
                                data-testid={`serve-card-${title.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-tint grid place-items-center shrink-0">
                                    <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <div className="text-slate-800 font-semibold">{title}</div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* OUR APPROACH --------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Our Approach
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            A clear, step-by-step journey
                        </h2>
                        <p className="mt-3 text-slate-600">
                            From first enquiry to ongoing support — a considered, transparent process.
                        </p>
                    </div>
                    <div className="grid gap-4 sm:gap-3 sm:grid-cols-2 lg:grid-cols-5">
                        {APPROACH.map(({ icon: Icon, title }, i) => (
                            <div key={title} className="relative flex lg:flex-col items-center lg:items-start gap-4 lg:gap-5"
                                data-testid={`approach-step-${i + 1}`}
                            >
                                <div className="relative h-14 w-14 rounded-2xl bg-brand-navy grid place-items-center shrink-0">
                                    <Icon className="h-6 w-6 text-white" strokeWidth={1.75} />
                                    <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-brand-royal text-white text-[11px] font-bold grid place-items-center">
                                        {i + 1}
                                    </span>
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900 leading-snug">{title}</div>
                                </div>
                                {i < APPROACH.length - 1 && (
                                    <div className="hidden lg:block absolute top-7 left-16 right-[-1rem] h-px bg-slate-200" aria-hidden />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA ------------------------------------------------------ */}
            <section className="bg-gradient-to-br from-brand-navy to-brand-royal text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Let&apos;s build financial progress together.
                    </h2>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button
                            className="bg-white text-brand-navy hover:bg-brand-soft rounded-full h-12 px-6 font-semibold"
                            onClick={() => navigate("/home")}
                            data-testid="about-cta-explore-btn"
                        >
                            Explore Products <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                            onClick={placeholderLoginToast}
                            data-testid="about-cta-customer-login"
                        >
                            Customer Login
                        </Button>
                    </div>
                </div>
            </section>

            {/* FOOTER (mirrors /home) ----------------------------------- */}
            <footer className="bg-brand-navy text-slate-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
                    <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <BrandMark size={40} variant="white" />
                            <p className="mt-5 text-sm leading-relaxed max-w-md text-slate-400">
                                Lokdhara Microfinance is focused on providing structured and accessible
                                financial services through transparent processes, clear records and
                                customer-oriented support.
                            </p>
                            <div className="mt-5 flex gap-3">
                                {[Facebook, Instagram, Linkedin, MessageCircle].map((Icon, i) => (
                                    <a key={i} href="/home"
                                        className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition"
                                        aria-label={`social-${i}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {Object.entries(FOOTER_LINKS).map(([col, items]) => (
                            <div key={col}>
                                <div className="text-white font-semibold text-sm mb-4">{col}</div>
                                <ul className="space-y-2 text-sm">
                                    {items.map((item) => (
                                        <li key={item}>
                                            <button
                                                onClick={() => {
                                                    if (item === "About Us") navigate("/about-us");
                                                    else navigate("/home");
                                                }}
                                                className="hover:text-white transition text-left"
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-xs text-slate-400 leading-relaxed max-w-3xl">
                        Product availability, eligibility, rates and terms are subject to approval and
                        applicable requirements.
                    </p>

                    <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                        <div>© {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.</div>
                        <div className="flex items-center gap-5">
                            <button onClick={() => navigate("/home")} className="hover:text-white">Privacy Policy</button>
                            <button onClick={() => navigate("/home")} className="hover:text-white">Terms &amp; Conditions</button>
                            <Link
                                to="/login"
                                className="text-slate-300 hover:text-white underline underline-offset-4"
                                data-testid="about-footer-staff-login"
                            >
                                Staff Login
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
